import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function SiteNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors whitespace-nowrap ${
      isActive ? "text-accent-600" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "bg-background-50/95 border-background-200 backdrop-blur"
          : "bg-background-50 border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
              <i className="ri-movie-2-line text-background-50 text-lg" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-base md:text-lg font-bold text-foreground-950 font-heading">
                国语对白做受69
              </span>
              <span className="text-[10px] md:text-[11px] text-foreground-500 tracking-wide">
                高清影音资源集锦
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <NavLink to="/" end className={navLinkClass}>
              首页
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-3 w-52">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <i className={`${cat.icon} text-base text-accent-500`} />
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/search" className={navLinkClass}>
              搜索
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              关于
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视作品"
                  className="w-52 rounded-full border border-background-200 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                />
              </div>
            </form>
            <Link
              to="/search"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="菜单"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品"
                className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </form>
          <div className="flex flex-col">
            <Link to="/" className="py-2.5 text-sm font-medium text-foreground-800 border-b border-background-100">
              首页
            </Link>
            <Link to="/search" className="py-2.5 text-sm font-medium text-foreground-800 border-b border-background-100">
              搜索
            </Link>
            <Link to="/about" className="py-2.5 text-sm font-medium text-foreground-800 border-b border-background-100">
              关于
            </Link>
            <div className="pt-2.5">
              <p className="text-xs text-foreground-500 mb-2">分类浏览</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 cursor-pointer"
                  >
                    <i className={`${cat.icon} text-accent-500`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}